import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import tmdbService from '../services/tmdbService';

const Favorites = () => {
  const [favorites, setFavorites] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadFavorites();
    
    // Listen for favorites changes
    const handleFavoritesChanged = () => {
      loadFavorites();
    };
    
    window.addEventListener('favoritesChanged', handleFavoritesChanged);
    
    return () => {
      window.removeEventListener('favoritesChanged', handleFavoritesChanged);
    };
  }, []);

  const loadFavorites = () => {
    const savedFavorites = JSON.parse(localStorage.getItem('favorites') || '[]');
    setFavorites(savedFavorites);
    setLoading(false);
  };

  const removeFromFavorites = (movieId) => {
    const updatedFavorites = favorites.filter(movie => movie.id !== movieId);
    setFavorites(updatedFavorites);
    localStorage.setItem('favorites', JSON.stringify(updatedFavorites));
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-950 flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-orange-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-xl text-gray-400">Loading favorites...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-950">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-gray-950 via-gray-900 to-gray-950 py-12 mb-8 relative overflow-hidden">
        {/* Animated Background Gradient */}
        <div className="absolute inset-0 bg-gradient-to-r from-orange-500/10 via-transparent to-pink-500/10 animate-pulse"></div>
        
        {/* Subtle Grid Pattern */}
        <div className="absolute inset-0 opacity-[0.03]" style={{backgroundImage: 'linear-gradient(rgba(255,255,255,0.1) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.1) 1px, transparent 1px)', backgroundSize: '100px 100px'}}></div>
        
        <div className="container mx-auto px-4 relative z-10">
          <div className="text-center max-w-4xl mx-auto">
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-3 lg:mb-4 leading-tight">
              <span className="bg-gradient-to-r from-white via-gray-200 to-gray-400 bg-clip-text text-transparent">
                My Favorites
              </span>
            </h1>
            <p className="text-lg sm:text-xl text-gray-400 max-w-xl mx-auto leading-relaxed">
              Your personally curated collection of beloved movies
            </p>
          </div>
        </div>
      </section>

      {/* Favorites Content */}
      <section className="container mx-auto px-4 pb-16">
        {favorites.length === 0 ? (
          <div className="text-center py-16">
            <div className="max-w-md mx-auto">
              <div className="text-6xl mb-4">❤️</div>
              <h2 className="text-2xl font-bold text-white mb-4">No favorites yet</h2>
              <p className="text-gray-400 mb-8">
                Start adding movies to your favorites to build your personal collection!
              </p>
              <Link 
                to="/movies" 
                className="inline-flex items-center justify-center bg-gradient-to-r from-orange-500 to-pink-500 hover:from-orange-600 hover:to-pink-600 text-white px-8 py-4 rounded-xl font-semibold text-lg transition-all duration-300 shadow-lg shadow-orange-500/30 hover:shadow-xl hover:shadow-orange-500/40 hover:-translate-y-1"
              >
                Explore Movies
              </Link>
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {favorites.map((movie) => (
              <div key={movie.id} className="group relative">
                <div className="bg-gray-900/50 backdrop-blur-sm rounded-xl overflow-hidden border border-white/10 hover:border-orange-500/30 transition-all duration-300 hover:shadow-xl hover:shadow-orange-500/20">
                  <Link to={`/movie/${movie.id}`} className="block">
                    <div className="relative aspect-[2/3] overflow-hidden">
                      <img
                        src={movie.poster || (movie.poster_path ? tmdbService.getImageUrl(movie.poster_path, 'w500') : '/placeholder-movie.jpg')}
                        alt={movie.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                    </div>
                    <div className="p-4">
                      <h3 className="font-bold text-white mb-2 line-clamp-2 group-hover:text-orange-400 transition-colors">
                        {movie.title}
                      </h3>
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-gray-400">
                          {movie.release_date?.split('-')[0] || movie.year || 'N/A'}
                        </span>
                        <div className="flex items-center gap-1">
                          <span className="text-yellow-400">⭐</span>
                          <span className="text-sm text-white">{movie.vote_average?.toFixed(1) || movie.rating || 'N/A'}</span>
                        </div>
                      </div>
                    </div>
                  </Link>
                  <button
                    onClick={() => removeFromFavorites(movie.id)}
                    className="absolute top-3 right-3 w-10 h-10 bg-red-500/90 backdrop-blur-sm rounded-full flex items-center justify-center text-white hover:bg-red-600 transition-all duration-300 opacity-0 group-hover:opacity-100 hover:scale-110"
                    aria-label="Remove from favorites"
                  >
                    <span className="text-lg">×</span>
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </section>
    </div>
  );
};

export default Favorites;
